import React, { useCallback, useMemo, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import getPlaceholderColors from '../../utils/placeholderColors';
import { NODE_UI, stopPropagation } from './nodeUiStyles';
import {
  EMPTY_FIELD_MSG,
  NO_SPECIAL_CHARS_MSG,
  formatPlaceholderKey,
  getDisplayPlaceholderKey,
  hasInvalidPlaceholderKeyChars,
} from './placeholderKeyUtils';
import useTracePlaceholder from '../Placeholders/useTracePlaceholder';

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})(({ fullWidth }) => ({
  width: fullWidth ? '100%' : 'auto',
  minWidth: 0,
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const ChipShell = styled('label', {
  shouldForwardProp: (prop) => ![
    'chipColor',
    'borderColor',
    'textColor',
    'hasError',
    'fullWidth',
    'isDisabled',
    'isReadOnly',
  ].includes(prop),
})(({ theme, chipColor, borderColor, textColor, hasError, fullWidth, isDisabled, isReadOnly }) => {
  const resolvedBorderColor = hasError ? theme.palette.error.main : borderColor;
  return {
    minHeight: NODE_UI.input.height,
    width: fullWidth ? '100%' : 'auto',
    maxWidth: '100%',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '5px 7px',
    borderRadius: NODE_UI.borderRadiusSm,
    border: `1px solid ${theme.palette.mode === 'dark' && !hasError ? `${resolvedBorderColor}66` : resolvedBorderColor}`,
    backgroundColor: chipColor,
    color: textColor || theme.palette.text.primary,
    whiteSpace: 'nowrap',
    fontSize: '11px',
    fontWeight: 500,
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    letterSpacing: '-0.01em',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 1px 2px rgba(0,0,0,0.2)'
      : '0 1px 2px rgba(0,0,0,0.04)',
    opacity: isDisabled ? 0.54 : 1,
    cursor: isDisabled ? 'not-allowed' : (isReadOnly ? 'default' : 'text'),
    transition: 'all 0.15s ease',
    overflow: 'hidden',
    '&:hover': {
      borderColor: isDisabled ? resolvedBorderColor : (hasError ? theme.palette.error.main : theme.palette.text.disabled),
    },
    '&:focus-within': {
      borderColor: hasError ? theme.palette.error.main : borderColor,
      boxShadow: hasError
        ? `0 0 0 2px ${theme.palette.error.main}22`
        : `0 0 0 2px ${borderColor}22`,
    },
  };
});

const ChipIcon = styled('span', {
  shouldForwardProp: (prop) => !['iconUrl', 'iconColor'].includes(prop),
})(({ iconUrl, iconColor }) => ({
  width: 12,
  height: 12,
  flexShrink: 0,
  backgroundColor: iconColor,
  maskImage: `url(${iconUrl})`,
  WebkitMaskImage: `url(${iconUrl})`,
  maskSize: 'contain',
  WebkitMaskSize: 'contain',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskPosition: 'center',
  WebkitMaskPosition: 'center',
}));

const Brace = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));

const ChipGlyphButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: 'transparent',
  border: 0,
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  '&:disabled': {
    cursor: 'inherit',
  },
});

const Input = styled('input')(({ theme }) => ({
  minWidth: 28,
  width: '100%',
  flex: 1,
  border: 0,
  outline: 0,
  padding: 0,
  margin: 0,
  background: 'transparent',
  color: 'inherit',
  font: 'inherit',
  letterSpacing: 'inherit',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&::placeholder': {
    color: theme.palette.text.disabled,
    opacity: 1,
  },
  '&:disabled': {
    cursor: 'not-allowed',
  },
}));

const NodeOutputPlaceholderChipInput = React.memo(function NodeOutputPlaceholderChipInput({
  label = 'Output placeholder',
  value = '',
  defaultValue,
  type = 'text',
  onChange,
  onBlur,
  error: externalError,
  helperText,
  placeholder = 'placeholder_key',
  disabled = false,
  readOnly = false,
  excludePrefix = '',
  fullWidth = true,
  sx,
  ariaLabel,
  onChipClick,
}) {
  const [localError, setLocalError] = useState('');
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const displayValue = getDisplayPlaceholderKey(value, defaultValue, excludePrefix);
  const hasError = !displayValue || !!localError || !!externalError;
  const errorMessage = !displayValue ? EMPTY_FIELD_MSG : localError || helperText || '';
  const colors = useMemo(() => getPlaceholderColors(type), [type]);
  const resolvedLabel = label === false || label === null || label === '' ? null : label;
  const { onChipClick: defaultOnChipClick } = useTracePlaceholder();
  const handleChipGlyphMouseDown = useCallback((e) => {
    if (!displayValue) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof onChipClick === 'function') onChipClick(displayValue, type);
    else defaultOnChipClick(displayValue, type);
  }, [displayValue, type, onChipClick, defaultOnChipClick]);

  const handleChange = useCallback((e) => {
    const raw = e.target.value;
    const containsInvalid = hasInvalidPlaceholderKeyChars(raw);

    if (containsInvalid) {
      setLocalError(NO_SPECIAL_CHARS_MSG);
      setTooltipOpen(true);
    } else {
      setLocalError('');
    }

    onChange?.(formatPlaceholderKey(raw, excludePrefix));
  }, [excludePrefix, onChange]);

  const handleBlur = useCallback((e) => {
    setTooltipOpen(false);
    onBlur?.(e);
  }, [onBlur]);

  const handleFocus = useCallback(() => {
    if (hasError && errorMessage) {
      setTooltipOpen(true);
    }
  }, [hasError, errorMessage]);

  return (
    <Root
      fullWidth={fullWidth}
      sx={sx}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {resolvedLabel && <Label>{resolvedLabel}</Label>}
      <Tooltip
        title={errorMessage}
        arrow
        placement="top"
        open={tooltipOpen && hasError && !!errorMessage}
        disableFocusListener
        disableHoverListener
        disableTouchListener
      >
        <ChipShell
          chipColor={colors.hover}
          borderColor={colors.default}
          textColor={colors.text}
          hasError={hasError}
          fullWidth={fullWidth}
          isDisabled={disabled}
          isReadOnly={readOnly}
          data-placeholder-key={displayValue || undefined}
          data-placeholder-role="output"
        >
          <ChipGlyphButton
            type="button"
            tabIndex={-1}
            onMouseDown={handleChipGlyphMouseDown}
            disabled={!displayValue}
            aria-label={displayValue ? `Trace placeholder ${displayValue}` : undefined}
          >
            <ChipIcon iconUrl={colors.icon} iconColor={colors.default} aria-hidden="true" />
            <Brace>{'${'}</Brace>
          </ChipGlyphButton>
          <Input
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            aria-label={ariaLabel || resolvedLabel || 'Output placeholder key'}
          />
          <Brace>{'}'}</Brace>
        </ChipShell>
      </Tooltip>
    </Root>
  );
});

export default NodeOutputPlaceholderChipInput;
